import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';

import { fetchCustomerDetailsByPhone } from '../../../../CallHistory/services/call-history.service';
import { formatPhoneNumber } from '../../../../../utils/Helpers';
import {
  ActiveCallDetail,
  IncomingCallDetail,
  PF_RC_INBOUND_PROJECT_ID_KEY,
  RING_CENTRAL_EVENTS,
  answerSoftphoneCall,
  ensureSoftphoneOnDialer,
  getInboundCallLogProjectId,
  getRingCentralIntegrationSnapshot,
  hasInboundCallContext,
  isActiveInboundCall,
  rejectSoftphoneCall,
  resolveActiveCallElapsedSeconds,
  storeInboundCallProjectId,
} from '../../../../../utils/ringCentralDialer';

import { isRingCentralEnabled } from './ringCentralConfig';

type OpenPhonePanelHandler = (_opts?: { goToDialer?: boolean }) => void;

type IncomingCallWidgetProps = {
  rcPanelExpanded: boolean;
  onOpenPhonePanel: OpenPhonePanelHandler;
};

type CallPartyDetail = {
  name?: string;
  phoneNumber?: string;
};

const resolveDisplayName = (
  crmName: string | null,
  party?: CallPartyDetail | null
): string => {
  if (crmName) return crmName;

  const rcName = party?.name?.trim();
  const phoneNumber = party?.phoneNumber?.trim() || '';
  if (rcName && rcName !== 'Unknown') {
    const nameDigits = formatPhoneNumber(rcName);
    const phoneDigits = formatPhoneNumber(phoneNumber);
    if (nameDigits && phoneDigits && nameDigits === phoneDigits) {
      return phoneNumber || rcName;
    }
    return rcName;
  }

  return phoneNumber || 'Unknown caller';
};

const tryAnswerCall = () => {
  answerSoftphoneCall();
  window.setTimeout(() => answerSoftphoneCall(), 300);
  window.setTimeout(() => answerSoftphoneCall(), 800);
};

const buildCustomerProfileUrl = (customerId: number) =>
  `${window.location.origin}/crm/view/customer/${customerId}`;

const openCustomerProfileInNewTab = (customerId: number) => {
  const url = buildCustomerProfileUrl(customerId);
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.focus();
  window.setTimeout(() => window.focus(), 0);
  window.setTimeout(() => window.focus(), 150);
};

const getProjectViewIdFromPathname = (pathname: string): string | null => {
  const segments = pathname.split('/').filter(Boolean);
  const viewIndex = segments.lastIndexOf('view');
  if (viewIndex === -1) return null;

  const parentSegment = segments[viewIndex - 1];
  if (parentSegment !== 'project' && parentSegment !== 'new-project') {
    return null;
  }

  return segments[viewIndex + 1] || null;
};

const formatCallElapsed = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const paddedMinutes = String(minutes).padStart(2, '0');
  const paddedSeconds = String(seconds).padStart(2, '0');
  if (hours > 0) {
    return `${hours}:${paddedMinutes}:${paddedSeconds}`;
  }
  return `${paddedMinutes}:${paddedSeconds}`;
};

const IncomingCallWidget = ({
  rcPanelExpanded,
  onOpenPhonePanel,
}: IncomingCallWidgetProps) => {
  const { pathname } = useLocation();
  const projectViewId = useMemo(
    () => getProjectViewIdFromPathname(pathname),
    [pathname]
  );

  const snapshot = getRingCentralIntegrationSnapshot();
  const [incoming, setIncoming] = useState<IncomingCallDetail | null>(
    () => snapshot.incomingCall
  );
  const [activeCall, setActiveCall] = useState<ActiveCallDetail | null>(
    () => snapshot.activeCall
  );
  const [linkedProjectId, setLinkedProjectId] = useState<number | null>(() =>
    getInboundCallLogProjectId()
  );
  const [crmName, setCrmName] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [lookupReady, setLookupReady] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const openedPanelForActiveCall = useRef<string | null>(null);
  const openedCustomerTabForCall = useRef<string | null>(null);

  const syncLinkedProjectId = useCallback(() => {
    setLinkedProjectId(getInboundCallLogProjectId());
  }, []);

  const openCustomerTabOnce = useCallback((phone: string, id: number) => {
    const key = formatPhoneNumber(phone) || String(id);
    if (!key || openedCustomerTabForCall.current === key) return;
    openedCustomerTabForCall.current = key;
    openCustomerProfileInNewTab(id);
  }, []);

  const clearAll = useCallback(() => {
    setIncoming(null);
    setActiveCall(null);
    setCrmName(null);
    setCustomerId(null);
    setLookupReady(false);
    openedPanelForActiveCall.current = null;
    openedCustomerTabForCall.current = null;
  }, []);

  useEffect(() => {
    const onIncoming = (event: Event) => {
      const detail = (event as CustomEvent<IncomingCallDetail>).detail;
      if (!detail) return;
      setIncoming(detail);
      setCrmName(null);
      setCustomerId(null);
      setLookupReady(false);
    };

    const onIncomingClear = () => setIncoming(null);

    const onCallEnd = () => clearAll();

    const onActiveCall = (event: Event) => {
      const detail = (event as CustomEvent<ActiveCallDetail | null>).detail;
      setActiveCall(detail ?? null);
      if (detail) {
        setIncoming(null);
      } else {
        syncLinkedProjectId();
      }
    };

    const onInboundProjectLinked = () => {
      syncLinkedProjectId();
    };

    const onStorage = (event: StorageEvent) => {
      if (event.key === PF_RC_INBOUND_PROJECT_ID_KEY) {
        syncLinkedProjectId();
      }
    };

    window.addEventListener(RING_CENTRAL_EVENTS.INCOMING, onIncoming);
    window.addEventListener(
      RING_CENTRAL_EVENTS.INCOMING_CLEAR,
      onIncomingClear
    );
    window.addEventListener(RING_CENTRAL_EVENTS.CALL_END, onCallEnd);
    window.addEventListener(RING_CENTRAL_EVENTS.ACTIVE_CALL, onActiveCall);
    window.addEventListener(
      RING_CENTRAL_EVENTS.INBOUND_PROJECT_LINKED,
      onInboundProjectLinked
    );
    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener(RING_CENTRAL_EVENTS.INCOMING, onIncoming);
      window.removeEventListener(
        RING_CENTRAL_EVENTS.INCOMING_CLEAR,
        onIncomingClear
      );
      window.removeEventListener(RING_CENTRAL_EVENTS.CALL_END, onCallEnd);
      window.removeEventListener(RING_CENTRAL_EVENTS.ACTIVE_CALL, onActiveCall);
      window.removeEventListener(
        RING_CENTRAL_EVENTS.INBOUND_PROJECT_LINKED,
        onInboundProjectLinked
      );
      window.removeEventListener('storage', onStorage);
    };
  }, [clearAll, syncLinkedProjectId]);

  useEffect(() => {
    if (!activeCall?.callId) {
      setElapsedSeconds(0);
      return undefined;
    }

    const tick = () => {
      const call = getRingCentralIntegrationSnapshot().activeCall;
      setElapsedSeconds(resolveActiveCallElapsedSeconds(call));
    };

    tick();
    const intervalId = window.setInterval(tick, 1000);
    return () => window.clearInterval(intervalId);
  }, [
    activeCall?.callId,
    activeCall?.connectedAtMs,
    activeCall?.durationSeconds,
  ]);

  useEffect(() => {
    const callId = activeCall?.callId;
    if (!callId || openedPanelForActiveCall.current === callId) return;
    openedPanelForActiveCall.current = callId;
    onOpenPhonePanel();
  }, [activeCall?.callId, onOpenPhonePanel]);

  const phoneToLookup = incoming?.phoneNumber?.trim() || '';

  useEffect(() => {
    if (!phoneToLookup) {
      setLookupReady(false);
      return undefined;
    }

    let cancelled = false;
    setLookupReady(false);

    void fetchCustomerDetailsByPhone(phoneToLookup)
      .then(result => {
        if (cancelled) return;
        if (result) {
          setCrmName(result.name);
          setCustomerId(result.customerId);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLookupReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [phoneToLookup, incoming?.callId]);

  const activePhoneToLookup = activeCall?.phoneNumber?.trim() || '';

  useEffect(() => {
    if (!activePhoneToLookup || crmName || customerId) return undefined;

    let cancelled = false;
    void fetchCustomerDetailsByPhone(activePhoneToLookup).then(result => {
      if (cancelled || !result) return;
      setCrmName(result.name);
      setCustomerId(result.customerId);
    });

    return () => {
      cancelled = true;
    };
  }, [activePhoneToLookup, activeCall?.callId, crmName, customerId]);

  const isRinging = Boolean(incoming);
  const isActive = Boolean(activeCall);
  const party: CallPartyDetail | null = incoming ?? activeCall;

  const displayName = useMemo(
    () => resolveDisplayName(crmName, party),
    [crmName, party]
  );

  const showIncomingLoading = isRinging && !lookupReady;
  const showIncomingCard = isRinging && lookupReady;
  const showActiveChip = isActive && !rcPanelExpanded;

  const showLinkButton =
    isRingCentralEnabled() &&
    Boolean(projectViewId) &&
    isActiveInboundCall(activeCall);

  const numericProjectId = projectViewId != null ? Number(projectViewId) : null;
  const isLinkedToThisProject =
    numericProjectId != null &&
    linkedProjectId != null &&
    linkedProjectId === numericProjectId;
  const isLinkedElsewhere =
    linkedProjectId != null &&
    numericProjectId != null &&
    linkedProjectId !== numericProjectId;

  const handleLinkToProject = () => {
    if (!projectViewId) return;
    if (hasInboundCallContext()) {
      syncLinkedProjectId();
      return;
    }
    storeInboundCallProjectId(projectViewId);
    setLinkedProjectId(getInboundCallLogProjectId());
  };

  if (!showIncomingLoading && !showIncomingCard && !showActiveChip) {
    return null;
  }

  const handleAccept = () => {
    if (customerId && incoming?.phoneNumber) {
      openCustomerTabOnce(incoming.phoneNumber, customerId);
    }
    tryAnswerCall();
    onOpenPhonePanel({ goToDialer: true });
    ensureSoftphoneOnDialer();
  };

  const handleDecline = () => {
    rejectSoftphoneCall();
  };

  const handleActiveChipClick = () => {
    onOpenPhonePanel();
  };

  if (showIncomingLoading) {
    return (
      <div
        className="pf-rc-incoming-call pf-rc-incoming-call--loading"
        role="status"
        aria-live="polite"
        aria-label="Incoming call"
      >
        <div className="pf-rc-incoming-call__pulse" aria-hidden />
        <div className="pf-rc-incoming-call__icon-wrap">
          <i className="pi pi-phone pf-rc-incoming-call__icon" aria-hidden />
        </div>
        <div className="pf-rc-incoming-call__body">
          <span className="pf-rc-incoming-call__label">Incoming call</span>
          <div className="pf-rc-incoming-call__shimmer-stack">
            <Skeleton
              width="10rem"
              height="1.1rem"
              className="pf-rc-incoming-call__shimmer"
            />
            <Skeleton
              width="7.5rem"
              height="0.85rem"
              className="pf-rc-incoming-call__shimmer"
            />
          </div>
        </div>
        <div className="pf-rc-incoming-call__actions">
          <Button
            type="button"
            size="small"
            label="Reject"
            icon="pi pi-times"
            severity="danger"
            outlined
            className="w-full"
            onClick={handleDecline}
          />
        </div>
      </div>
    );
  }

  if (showIncomingCard) {
    return (
      <div
        className="pf-rc-incoming-call"
        role="dialog"
        aria-label="Incoming call"
      >
        <div className="pf-rc-incoming-call__pulse" aria-hidden />
        <div className="pf-rc-incoming-call__icon-wrap">
          <i className="pi pi-phone pf-rc-incoming-call__icon" aria-hidden />
        </div>
        <div className="pf-rc-incoming-call__body">
          <span className="pf-rc-incoming-call__label">Incoming call</span>
          <span className="pf-rc-incoming-call__name">{displayName}</span>
          {party?.phoneNumber ? (
            <span className="pf-rc-incoming-call__phone">
              {party.phoneNumber}
            </span>
          ) : null}
        </div>
        <div className="pf-rc-incoming-call__actions pf-rc-incoming-call__actions--row">
          <Button
            type="button"
            size="small"
            label="Reject"
            icon="pi pi-times"
            severity="danger"
            outlined
            onClick={handleDecline}
          />
          <Button
            type="button"
            size="small"
            label="Answer"
            icon="pi pi-phone"
            onClick={handleAccept}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="pf-rc-active-call-chip">
      <button
        type="button"
        className="pf-rc-active-call-chip__main"
        aria-label={`Active call with ${displayName}, ${formatCallElapsed(elapsedSeconds)} elapsed. Open phone panel.`}
        onClick={handleActiveChipClick}
      >
        <span className="pf-rc-active-call-chip__dot" aria-hidden />
        <span className="pf-rc-active-call-chip__content">
          <span className="pf-rc-active-call-chip__label-row">
            <span className="pf-rc-active-call-chip__label">
              Call in progress
            </span>
            <span className="pf-rc-active-call-chip__duration" aria-hidden>
              {formatCallElapsed(elapsedSeconds)}
            </span>
          </span>
          <span className="pf-rc-active-call-chip__name">{displayName}</span>
          {party?.phoneNumber ? (
            <span className="pf-rc-active-call-chip__phone">
              {party.phoneNumber}
            </span>
          ) : null}
        </span>
        <i
          className="pi pi-chevron-right pf-rc-active-call-chip__chevron"
          aria-hidden
        />
      </button>
      {showLinkButton ? (
        <Button
          type="button"
          size="small"
          label={
            isLinkedToThisProject
              ? 'Linked'
              : isLinkedElsewhere
                ? 'Linked elsewhere'
                : 'Link Call to Project'
          }
          icon={isLinkedToThisProject ? 'pi pi-check' : 'pi pi-link'}
          severity={isLinkedToThisProject ? 'success' : undefined}
          disabled={isLinkedToThisProject || isLinkedElsewhere}
          className="pf-rc-active-call-chip__link-btn w-full"
          onClick={handleLinkToProject}
        />
      ) : null}
    </div>
  );
};

export default IncomingCallWidget;
